import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  List,
  CircularProgress,
  LinearProgress,
  Fade,
  useTheme,
  alpha,
  TextField,
  InputAdornment,
  Tabs,
  Tab
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { toast } from 'react-toastify';
import { api } from '../services/AuthService';
import { useAuth } from '../services/AuthService';

const PEDIDOS_PAGE_SIZE = 50;
const REFUND_RETRY_AFTER_MS = 5 * 60 * 1000;

const isRefundRetryable = (pedido) => {
  const status = String(pedido?.refund_status || '').toLowerCase();
  if (!pedido?.refund_idempotency_key || !(Number(pedido?.refund_attempt_amount) > 0)) return false;
  if (status === 'refund_pending') return true;
  if (status !== 'processing') return false;
  const updatedAt = new Date(pedido.updated_at || 0).getTime();
  return Date.now() - updatedAt >= REFUND_RETRY_AFTER_MS;
};

function Pedidos() {
  const theme = useTheme();
  const [pedidos, setPedidos] = useState([]);
  const [lanchonete, setLanchonete] = useState(null);
  const [filter, setFilter] = useState('all');
  const [dayFilter, setDayFilter] = useState('hoje'); // 'todos', 'hoje', 'ontem'
  const [originFilter, setOriginFilter] = useState('all'); // 'all', 'app', 'mesas'
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('orders');
  const [productSearch, setProductSearch] = useState('');
  const [productSales, setProductSales] = useState([]);
  const [productSalesSummary, setProductSalesSummary] = useState({
    different_products: 0, quantity_sold: 0, orders_count: 0, sales_total: 0
  });
  const [productSalesLoading, setProductSalesLoading] = useState(false);
  const [selectedPedido, setSelectedPedido] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundForm, setRefundForm] = useState({ amount: '', reason: '' });
  const [refunding, setRefunding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [deliveryCallOpen, setDeliveryCallOpen] = useState(false);
  const [creatingDeliveryCall, setCreatingDeliveryCall] = useState(false);
  const [deliveryCallForm, setDeliveryCallForm] = useState({
    customer_name: '', customer_phone: '', delivery_address: '', house_number: '', reference: '', notes: ''
  });
  const [payingCourierId, setPayingCourierId] = useState(null);
  const [whatsappDeliveryReport, setWhatsappDeliveryReport] = useState(null);
  const silentRequestInFlight = useRef(false);
  const { isAdminMaster, getLanchoneteId } = useAuth();

  const filterPedidos = (pedidos) => {
    let filtered = [...pedidos];

    // Filtro por dia
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (dayFilter !== 'todos') {
      filtered = filtered.filter(pedido => {
        const pedidoDate = new Date(pedido.created_at);
        if (dayFilter === 'hoje') {
          return pedidoDate.toDateString() === today.toDateString();
        } else if (dayFilter === 'ontem') {
          return pedidoDate.toDateString() === yesterday.toDateString();
        }
        return true;
      });
    }

    // Filtro por origem
    if (originFilter !== 'all') {
      filtered = filtered.filter(pedido => {
        if (originFilter === 'app') {
          return !pedido.mesa_id;
        } else if (originFilter === 'mesas') {
          return !!pedido.mesa_id;
        }
        return true;
      });
    }

    // Filtro por termo de pesquisa
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(pedido => 
        pedido.id.toString().includes(term) ||
        (pedido.lanchonete_order_number && pedido.lanchonete_order_number.toString().includes(term)) ||
        (pedido.customer_name && pedido.customer_name.toLowerCase().includes(term)) ||
        (pedido.motoboy_name && pedido.motoboy_name.toLowerCase().includes(term)) ||
        (pedido.customer_phone && pedido.customer_phone.includes(term)) ||
        (pedido.mesa_id && pedido.mesa_id.toString().includes(term))
      );
    }

    return filtered;
  };

  const loadLanchonete = useCallback(async () => {
    try {
      const lanchoneteId = getLanchoneteId();
      if (lanchoneteId) {
        const response = await api.get(`/lanchonetes/${lanchoneteId}`);
        setLanchonete(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar dados da lanchonete:', error);
    }
  }, [getLanchoneteId]);

  const loadPedidos = useCallback(async ({ silent = false } = {}) => {
    if (silent && silentRequestInFlight.current) return;
    if (silent) silentRequestInFlight.current = true;
    try {
      if (!silent) setLoading(true);
      const params = new URLSearchParams();
      
      if (!isAdminMaster()) {
        const lanchoneteId = getLanchoneteId();
        if (!lanchoneteId) {
          setPedidos([]);
          if (!silent) toast.error('Usuario sem lanchonete associada');
          return;
        }
        params.set('lanchonete_id', lanchoneteId);
      }
      
      if (filter !== 'all') {
        params.set('status', filter);
      }

      params.set('limit', PEDIDOS_PAGE_SIZE);
      params.set('offset', page * PEDIDOS_PAGE_SIZE);

      if (dayFilter !== 'todos') {
        const selectedDate = new Date();
        if (dayFilter === 'ontem') selectedDate.setDate(selectedDate.getDate() - 1);
        const localDate = [
          selectedDate.getFullYear(),
          String(selectedDate.getMonth() + 1).padStart(2, '0'),
          String(selectedDate.getDate()).padStart(2, '0')
        ].join('-');
        params.set('date', localDate);
      }

      const query = params.toString();
      const response = await api.get(query ? `/pedidos?${query}` : '/pedidos');
      setPedidos(response.data);
      setHasNextPage(response.data.length === PEDIDOS_PAGE_SIZE);
    } catch (error) {
      if (!silent) toast.error('Erro ao carregar pedidos');
      console.error('Erro ao carregar pedidos:', error);
    } finally {
      if (silent) silentRequestInFlight.current = false;
      if (!silent) setLoading(false);
    }
  }, [isAdminMaster, getLanchoneteId, filter, dayFilter, page]);

  const loadWhatsappDeliveryReport = useCallback(async () => {
    try {
      const response = await api.get('/delivery-calls/report');
      setWhatsappDeliveryReport(response.data);
    } catch (error) {
      console.error('Erro ao carregar prestacao de contas do WhatsApp:', error);
    }
  }, []);

  const loadProductSales = useCallback(async () => {
    try {
      setProductSalesLoading(true);
      const params = new URLSearchParams();
      params.set('origin', originFilter);
      if (dayFilter !== 'todos') {
        const selectedDate = new Date();
        if (dayFilter === 'ontem') selectedDate.setDate(selectedDate.getDate() - 1);
        params.set('date', [
          selectedDate.getFullYear(),
          String(selectedDate.getMonth() + 1).padStart(2, '0'),
          String(selectedDate.getDate()).padStart(2, '0')
        ].join('-'));
      }
      if (isAdminMaster()) {
        const selectedStore = getLanchoneteId();
        if (selectedStore) params.set('lanchonete_id', selectedStore);
      }
      const response = await api.get(`/pedidos/report/products?${params.toString()}`);
      setProductSales(Array.isArray(response.data?.products) ? response.data.products : []);
      setProductSalesSummary(response.data?.summary || {
        different_products: 0, quantity_sold: 0, orders_count: 0, sales_total: 0
      });
    } catch (error) {
      console.error('Erro ao carregar produtos vendidos:', error);
      toast.error(error.response?.data?.error || 'Erro ao carregar produtos vendidos');
    } finally {
      setProductSalesLoading(false);
    }
  }, [dayFilter, originFilter, isAdminMaster, getLanchoneteId]);

  useEffect(() => {
    loadPedidos();
  }, [loadPedidos]);

  useEffect(() => {
    loadWhatsappDeliveryReport();
  }, [loadWhatsappDeliveryReport]);

  useEffect(() => {
    loadLanchonete();
  }, [loadLanchonete]);

  useEffect(() => {
    if (activeTab === 'products') loadProductSales();
  }, [activeTab, loadProductSales]);

  useEffect(() => {
    const refreshIfVisible = () => {
      if (!document.hidden) loadPedidos({ silent: true });
    };
    const interval = setInterval(() => {
      refreshIfVisible();
    }, 30000);
    document.addEventListener('visibilitychange', refreshIfVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', refreshIfVisible);
    };
  }, [loadPedidos]);



  const getStatusColor = (status) => {
    switch (status) {
      case 'pendente':
      case 'pending':
        return 'warning';
      case 'aceito':
        return 'info';
      case 'preparando':
      case 'preparing':
        return 'secondary';
      case 'saiu_entrega':
      case 'delivering':
        return 'primary';
      case 'entregue':
      case 'delivered':
        return 'success';
      case 'cancelado':
      case 'cancelled':
        return 'error';
      default: return 'default';
    }
  };

  const getPaymentMethodText = (paymentMethod) => {
    switch (paymentMethod) {
      case 'mercadopago': return 'Mercado Pago (Online)';
      case 'pix': return 'PIX';
      case 'money': return '💵 Dinheiro';
      case 'pickup': return '🏪 Retirada';
      case 'local': return '🍽️ Comer no local';
      default: return `💳 ${paymentMethod || 'Não informado'}`;
    }
  };

  const getPaymentMethodColor = (paymentMethod) => {
    switch (paymentMethod) {
      case 'mercadopago': return 'primary';
      case 'pix': return 'info';
      case 'money': return 'success';
      case 'pickup': return 'secondary';
      case 'local': return 'info';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pendente':
      case 'pending':
        return <AccessTimeIcon />;
      case 'aceito':
        return <CheckCircleIcon />;
      case 'preparando':
      case 'preparing':
        return <RestaurantIcon />;
      case 'saiu_entrega':
      case 'delivering':
        return <TwoWheelerIcon />;
      case 'entregue':
      case 'delivered':
        return <CheckCircleIcon />;
      case 'cancelado':
      case 'cancelled':
        return <ReceiptIcon />;
      default: return <ReceiptIcon />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pendente':
      case 'pending':
        return 'Pendente';
      case 'aceito':
        return 'Aceito';
      case 'preparando':
      case 'preparing':
        return 'Preparando';
      case 'pronto':
      case 'pedido_pronto':
      case 'ready':
        return 'Pedido Pronto';
      case 'saiu_entrega':
      case 'delivering':
        return 'Saiu para Entrega';
      case 'entregue':
      case 'delivered':
        return 'Entregue';
      case 'cancelado':
      case 'cancelled':
        return 'Cancelado';
      default: return status;
    }
  };

  const handleViewDetails = async (pedido) => {
    try {
      const response = await api.get(`/pedidos/${pedido.id}`);
      const pedidoData = response.data;
      
      // Ajuste técnico: Garantimos que os itens existam independente da letra maiúscula vinda do banco
      const dadosFormatados = {
        ...pedidoData,
        itens_corrigidos: pedidoData.items || pedidoData.Itens || pedidoData.itens || []
      };

      setSelectedPedido(dadosFormatados);
      setDetailDialogOpen(true);
    } catch (error) {
      console.error('Erro ao buscar detalhes do pedido:', error);
      toast.error('Erro ao carregar detalhes do pedido');
    }
  };

  const handleUpdateStatus = async (pedidoId, status) => {
    try {
      await api.patch(`/pedidos/${pedidoId}/status`, { status });
      toast.success('Status atualizado com sucesso!');
      loadPedidos();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const openRefundDialog = () => {
    const total = Number(selectedPedido?.total_amount ?? selectedPedido?.total) || 0;
    const refunded = Number(selectedPedido?.refunded_amount) || 0;
    const pendingAttempt = isRefundRetryable(selectedPedido)
      ? Number(selectedPedido?.refund_attempt_amount)
      : 0;
    const remaining = Math.max(0, total - refunded);
    const amount = pendingAttempt > 0 ? pendingAttempt : remaining;
    setRefundForm({ amount: amount.toFixed(2), reason: '' });
    setRefundDialogOpen(true);
  };

  const handleRefund = async () => {
    if (refunding || !selectedPedido) return;
    const amount = Number(String(refundForm.amount).replace(',', '.'));
    if (!Number.isFinite(amount) || amount <= 0) return toast.error('Informe um valor valido.');
    if (!refundForm.reason.trim()) return toast.error('Informe o motivo da devolucao.');
    setRefunding(true);
    try {
      const response = await api.post(`/pedidos/${selectedPedido.id}/refund`, {
        amount,
        reason: refundForm.reason.trim()
      });
      toast.success(response.data.message || 'Devolucao realizada.');
      setSelectedPedido((current) => ({ ...current, ...(response.data.pedido || {}) }));
      setRefundDialogOpen(false);
      await loadPedidos();
    } catch (error) {
      if (error.response?.data?.pedido) {
        setSelectedPedido((current) => ({ ...current, ...error.response.data.pedido }));
      }
      toast.error(error.response?.data?.error || 'Nao foi possivel devolver o pagamento.');
    } finally {
      setRefunding(false);
    }
  };

  const handleCreateDeliveryCall = async () => {
    if (creatingDeliveryCall) return;
    setCreatingDeliveryCall(true);
    try {
      const response = await api.post('/delivery-calls', deliveryCallForm);
      toast.success(response.data.message || 'Motoboys avisados com sucesso.');
      setDeliveryCallOpen(false);
      setDeliveryCallForm({ customer_name: '', customer_phone: '', delivery_address: '', house_number: '', reference: '', notes: '' });
      await loadWhatsappDeliveryReport();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Nao foi possivel chamar o motoboy.');
    } finally {
      setCreatingDeliveryCall(false);
    }
  };

  const handleMarkCourierPaid = async (motoboy) => {
    if (payingCourierId || Number(motoboy.total_a_receber || 0) <= 0) return;
    if (!window.confirm(`Confirmar que R$ ${Number(motoboy.total_a_receber || 0).toFixed(2)} ja foi pago para ${motoboy.motoboy_name}?`)) return;
    setPayingCourierId(motoboy.motoboy_id);
    try {
      const response = await api.post(`/delivery-calls/report/${motoboy.motoboy_id}/mark-paid`);
      toast.success(response.data.message || 'Pagamento ao motoboy registrado.');
      await loadWhatsappDeliveryReport();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Nao foi possivel registrar o pagamento ao motoboy.');
    } finally {
      setPayingCourierId(null);
    }
  };

  const visiblePedidos = filterPedidos(pedidos);
  const visibleProductSales = productSales.filter((item) => {
    const term = productSearch.trim().toLowerCase();
    if (!term) return true;
    return String(item.produto_name || '').toLowerCase().includes(term)
      || String(item.categoria_name || '').toLowerCase().includes(term)
      || String(item.lanchonete_name || '').toLowerCase().includes(term);
  });
  const pedidosPendentes = visiblePedidos.filter((pedido) =>
    ['pendente', 'pending'].includes(String(pedido.status || '').toLowerCase())
  );
  const pedidosEmPreparo = visiblePedidos.filter((pedido) =>
    ['aceito', 'preparando', 'preparing'].includes(String(pedido.status || '').toLowerCase())
  );

  const handleMarkAllReady = async () => {
    if (bulkUpdating || pedidosEmPreparo.length === 0) return;
    if (!window.confirm(`Marcar os ${pedidosEmPreparo.length} pedidos visiveis como prontos?`)) return;

    setBulkUpdating(true);
    let successCount = 0;
    const failures = [];
    try {
      // Lotes pequenos protegem o servidor, e cada falha fica isolada para nao
      // impedir que os demais pedidos sejam atualizados.
      for (let index = 0; index < pedidosEmPreparo.length; index += 4) {
        const batch = pedidosEmPreparo.slice(index, index + 4);
        const results = await Promise.allSettled(
          batch.map((pedido) => api.patch(`/pedidos/${pedido.id}/status`, { status: 'pronto' }))
        );
        for (const [resultIndex, result] of results.entries()) {
          if (result.status === 'fulfilled') successCount += 1;
          else failures.push(batch[resultIndex].id);
        }
      }
      if (successCount) toast.success(`${successCount} pedido(s) marcado(s) como pronto(s).`);
      if (failures.length) toast.error(`${failures.length} pedido(s) nao foram atualizados e continuam na lista.`);
      await loadPedidos({ silent: true });
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleAcceptAll = async () => {
    if (bulkUpdating || pedidosPendentes.length === 0) return;
    if (!window.confirm(`Aceitar os ${pedidosPendentes.length} pedidos pendentes visiveis?`)) return;

    setBulkUpdating(true);
    let successCount = 0;
    const failures = [];
    try {
      for (let index = 0; index < pedidosPendentes.length; index += 4) {
        const batch = pedidosPendentes.slice(index, index + 4);
        const results = await Promise.allSettled(
          batch.map((pedido) => api.patch(`/pedidos/${pedido.id}/status`, { status: 'aceito' }))
        );
        for (const [resultIndex, result] of results.entries()) {
          if (result.status === 'fulfilled') successCount += 1;
          else failures.push(batch[resultIndex].id);
        }
      }
      if (successCount) toast.success(`${successCount} pedido(s) aceito(s).`);
      if (failures.length) toast.error(`${failures.length} pedido(s) nao puderam ser aceitos.`);
      await loadPedidos({ silent: true });
    } finally {
      setBulkUpdating(false);
    }
  };


  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        sx={{ 
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          flexDirection: 'column',
          gap: 3
        }}
      >
        <Fade in={true}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress 
              size={80} 
              thickness={4}
              sx={{ 
                color: theme.palette.primary.contrastText,
                mb: 2
              }} 
            />
            <Typography variant="h6" color="inherit">
              Carregando pedidos...
            </Typography>
            <Typography variant="body2" color="inherit" sx={{ opacity: 0.8 }}>
              Preparando suas informações
            </Typography>
          </Box>
        </Fade>
        <LinearProgress 
          sx={{ 
            width: '200px',
            bgcolor: alpha(theme.palette.primary.contrastText, 0.2),
            '& .MuiLinearProgress-bar': {
              bgcolor: theme.palette.primary.contrastText
            }
          }} 
        />
      </Box>
    );
  }

  return (
    <Box>
      {activeTab === 'orders' && whatsappDeliveryReport && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 3, bgcolor: '#f0fdf4' }}>
          <Typography fontWeight="bold" color="success.dark">Chamados avulsos de motoboy</Typography>
          <Box display="flex" gap={3} flexWrap="wrap" mt={1}>
            <Typography variant="body2"><strong>{whatsappDeliveryReport.total_corridas || 0}</strong> corrida(s) aceita(s)</Typography>
            <Typography variant="body2"><strong>{whatsappDeliveryReport.corridas_entregues || 0}</strong> entregue(s)</Typography>
            <Typography variant="body2"><strong>R$ {Number(whatsappDeliveryReport.total_a_pagar || 0).toFixed(2)}</strong> devido aos motoboys</Typography>
          </Box>
          {whatsappDeliveryReport.por_motoboy?.length > 0 && (
            <Box display="flex" gap={1} flexDirection="column" alignItems="flex-start" mt={1.5}>
              {whatsappDeliveryReport.por_motoboy.map((motoboy) => (
                <Box key={motoboy.motoboy_id} display="flex" gap={1} alignItems="center" flexWrap="wrap">
                  <Chip
                    color={Number(motoboy.total_a_receber || 0) > 0 ? 'warning' : 'success'}
                    variant="outlined"
                    label={`${motoboy.motoboy_name}: ${motoboy.entregues} entregue(s) - falta pagar R$ ${Number(motoboy.total_a_receber || 0).toFixed(2)}`}
                  />
                  {Number(motoboy.total_a_receber || 0) > 0 && (
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      disabled={Boolean(payingCourierId)}
                      onClick={() => handleMarkCourierPaid(motoboy)}
                    >
                      {payingCourierId === motoboy.motoboy_id ? 'Registrando...' : 'Ja pago'}
                    </Button>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      )}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h4" fontWeight="bold">Pedidos</Typography>
        
        <Box display="flex" gap={1} flexWrap="wrap">
          {activeTab === 'orders' && !isAdminMaster() && String(lanchonete?.delivery_mode || '').toUpperCase() === 'APP' && (
            <Button variant="contained" color="secondary" size="small" startIcon={<TwoWheelerIcon />} onClick={() => setDeliveryCallOpen(true)}>
              Chamar motoboy
            </Button>
          )}
          {activeTab === 'orders' && pedidosPendentes.length > 0 && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={bulkUpdating ? <CircularProgress size={15} color="inherit" /> : <CheckCircleIcon />}
              onClick={handleAcceptAll}
              disabled={bulkUpdating}
            >
              Aceitar todos ({pedidosPendentes.length})
            </Button>
          )}
          {activeTab === 'orders' && pedidosEmPreparo.length > 0 && (
            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={bulkUpdating ? <CircularProgress size={15} color="inherit" /> : <CheckCircleIcon />}
              onClick={handleMarkAllReady}
              disabled={bulkUpdating}
            >
              Marcar todos prontos ({pedidosEmPreparo.length})
            </Button>
          )}
          <Box display="flex" gap={0.5} ml={1}>
            <Button
              variant={dayFilter === 'todos' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => { setPage(0); setDayFilter('todos'); }}
            >
              TODOS
            </Button>
            <Button
              variant={dayFilter === 'hoje' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => { setPage(0); setDayFilter('hoje'); }}
            >
              HOJE
            </Button>
            <Button
              variant={dayFilter === 'ontem' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => { setPage(0); setDayFilter('ontem'); }}
            >
              ONTEM
            </Button>
          </Box>
        </Box>
      </Box>

      <Paper variant="outlined" sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value="orders" icon={<ReceiptIcon />} iconPosition="start" label="Lista de pedidos" />
          <Tab value="products" icon={<Inventory2Icon />} iconPosition="start" label="Produtos vendidos" />
        </Tabs>
      </Paper>

      <Box display="flex" gap={2} mb={3} alignItems="center" flexWrap="wrap">
        <TextField
          placeholder={activeTab === 'orders' ? 'Pesquisar pedido' : 'Pesquisar produto ou categoria'}
          size="small"
          value={activeTab === 'orders' ? searchTerm : productSearch}
          onChange={(e) => activeTab === 'orders' ? setSearchTerm(e.target.value) : setProductSearch(e.target.value)}
          sx={{ minWidth: 250, bgcolor: 'white' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Origem</InputLabel>
          <Select
            value={originFilter}
            onChange={(e) => setOriginFilter(e.target.value)}
            label="Origem"
            sx={{ bgcolor: 'white' }}
          >
            <MenuItem value="all">Todos</MenuItem>
            <MenuItem value="app">Aplicativo</MenuItem>
            <MenuItem value="mesas">Mesas</MenuItem>
          </Select>
        </FormControl>

        {activeTab === 'orders' && <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Filtrar por Status</InputLabel>
          <Select
            value={filter}
            onChange={(e) => { setPage(0); setFilter(e.target.value); }}
            label="Filtrar por Status"
            sx={{ bgcolor: 'white' }}
          >
            <MenuItem value="all">Todos os Status</MenuItem>
            <MenuItem value="pendente">Pendentes</MenuItem>
            <MenuItem value="aceito">Aceitos</MenuItem>
            <MenuItem value="preparando">Preparando</MenuItem>
            <MenuItem value="saiu_entrega">Em Entrega</MenuItem>
            <MenuItem value="entregue">Entregues</MenuItem>
            <MenuItem value="cancelado">Cancelados</MenuItem>
          </Select>
        </FormControl>}
      </Box>

      {activeTab === 'products' && (
        <Box>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              ['Produtos diferentes', productSalesSummary.different_products || 0],
              ['Unidades vendidas', productSalesSummary.quantity_sold || 0],
              ['Pedidos contabilizados', productSalesSummary.orders_count || 0],
              ['Valor em produtos', `R$ ${Number(productSalesSummary.sales_total || 0).toFixed(2)}`]
            ].map(([label, value]) => (
              <Grid item xs={12} sm={6} md={3} key={label}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Typography variant="h5" fontWeight={900}>{value}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
            {productSalesLoading && <LinearProgress />}
            <Table>
              <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Produto</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Categoria</TableCell>
                  {isAdminMaster() && <TableCell sx={{ fontWeight: 'bold' }}>Lanchonete</TableCell>}
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Quantidade vendida</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Pedidos</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Valor vendido</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!productSalesLoading && visibleProductSales.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={isAdminMaster() ? 6 : 5} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">Nenhum produto vendido neste periodo.</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {visibleProductSales.map((item) => (
                  <TableRow key={`${item.lanchonete_id}-${item.produto_id || item.produto_name}`} hover>
                    <TableCell><Typography fontWeight={800}>{item.produto_name}</Typography></TableCell>
                    <TableCell><Chip size="small" variant="outlined" label={item.categoria_name || 'Sem categoria'} /></TableCell>
                    {isAdminMaster() && <TableCell>{item.lanchonete_name || `#${item.lanchonete_id}`}</TableCell>}
                    <TableCell align="center">
                      <Typography variant="h6" color="primary" fontWeight={900}>{Number(item.quantity_sold || 0)}</Typography>
                    </TableCell>
                    <TableCell align="center">{Number(item.orders_count || 0)}</TableCell>
                    <TableCell align="right"><Typography fontWeight={800}>R$ {Number(item.sales_total || 0).toFixed(2)}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <TableContainer component={Paper} sx={{ display: activeTab === 'orders' ? 'block' : 'none', borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
        <Table>
          <TableHead sx={{ bgcolor: '#f8f9fa' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Cliente</TableCell>
              {!isAdminMaster() && <TableCell sx={{ fontWeight: 'bold' }}>Lanchonete</TableCell>}
              <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Pagamento</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Entregador</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Data</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visiblePedidos.map((pedido) => (
              <TableRow key={pedido.id} hover>
                <TableCell><Typography variant="subtitle2" fontWeight="bold">#{pedido.lanchonete_order_number || pedido.id}</Typography></TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">{pedido.customer_name || 'Cliente não identificado'}</Typography>
                  <Typography variant="caption" color="textSecondary">{pedido.customer_phone}</Typography>
                </TableCell>
                {!isAdminMaster() && (
                  <TableCell>
                    <Typography variant="body2">{pedido.lanchonete_name}</Typography>
                  </TableCell>
                )}
                <TableCell>
                  <Box>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>Pedido</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      R$ {(pedido.total_amount - (pedido.delivery_fee || 0)).toFixed(2)}
                    </Typography>
                    {pedido.delivery_fee > 0 && (
                      <>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>Taxa separada: R$ {pedido.delivery_fee.toFixed(2)}</Typography>
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          Total: R$ {pedido.total_amount.toFixed(2)}
                        </Typography>
                      </>
                    )}
                    {!pedido.delivery_fee && (
                      <Typography variant="body2" fontWeight="bold" color="primary">
                        Total: R$ {pedido.total_amount.toFixed(2)}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Chip 
                      label={getPaymentMethodText(pedido.payment_method)}
                      size="small"
                      color={getPaymentMethodColor(pedido.payment_method)}
                      sx={{ mb: 0.5 }}
                    />
                    {pedido.payment_status === 'paid' && (
                      <Chip
                        label="Pago"
                        size="small"
                        color="success"
                        sx={{ mb: 0.5, ml: 0.5, fontWeight: 'bold' }}
                      />
                    )}
                    {pedido.payment_status === 'refunded' && (
                      <Chip label="Devolvido" size="small" color="warning" sx={{ mb: 0.5, ml: 0.5, fontWeight: 'bold' }} />
                    )}
                    {pedido.payment_status === 'partially_refunded' && (
                      <Chip label="Devolucao parcial" size="small" color="warning" sx={{ mb: 0.5, ml: 0.5, fontWeight: 'bold' }} />
                    )}
                    {(pedido.needs_change === 1 || pedido.needs_change === true) && pedido.change_amount > 0 && (
                      <Typography variant="caption" color="success.main" sx={{ fontWeight: 'bold', display: 'block' }}>
                        💸 Troco: R$ {pedido.change_amount.toFixed(2)}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    icon={getStatusIcon(pedido.status)}
                    label={getStatusText(pedido.status)}
                    color={getStatusColor(pedido.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {pedido.motoboy_name ? (
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <TwoWheelerIcon color="primary" fontSize="small" />
                      <Box>
                        <Typography variant="body2" fontWeight="bold">{pedido.motoboy_name}</Typography>
                        <Typography variant="caption" color="text.secondary">Corrida aceita</Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.secondary">Aguardando entregador</Typography>
                  )}
                </TableCell>
                <TableCell><Typography variant="caption">{formatDate(pedido.created_at)}</Typography></TableCell>
                <TableCell align="right">
                  <Box display="flex" gap={1} justifyContent="flex-end">
                    <Button
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleViewDetails(pedido)}
                    >
                      Ver
                    </Button>

                    {pedido.status === 'pendente' && (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleUpdateStatus(pedido.id, 'aceito')}
                      >
                        Aceitar
                      </Button>
                    )}

                    {['aceito', 'preparando'].includes(pedido.status) && (
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        onClick={() => handleUpdateStatus(pedido.id, 'pronto')}
                      >
                        Pronto
                      </Button>
                    )}

                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={deliveryCallOpen} onClose={() => !creatingDeliveryCall && setDeliveryCallOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Chamar motoboy do aplicativo</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Chamado avulso para uma entrega recebida fora do sistema, como pelo WhatsApp. Isto nao cria nem altera pedido.
          </Typography>
          <Grid container spacing={2}>
            {[
              ['customer_name', 'Nome do cliente *'], ['customer_phone', 'WhatsApp do cliente'],
              ['delivery_address', 'Endereco da entrega *'], ['house_number', 'Numero'],
              ['reference', 'Referencia'], ['notes', 'Observacao']
            ].map(([name, label]) => (
              <Grid item xs={12} sm={name === 'customer_name' || name === 'delivery_address' || name === 'notes' ? 12 : 6} key={name}>
                <TextField fullWidth label={label} multiline={name === 'notes'} minRows={name === 'notes' ? 2 : undefined}
                  value={deliveryCallForm[name]} onChange={(event) => setDeliveryCallForm((current) => ({ ...current, [name]: event.target.value }))} />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeliveryCallOpen(false)} disabled={creatingDeliveryCall}>Cancelar</Button>
          <Button variant="contained" color="secondary" onClick={handleCreateDeliveryCall}
            disabled={creatingDeliveryCall || !deliveryCallForm.customer_name.trim() || !deliveryCallForm.delivery_address.trim()}>
            {creatingDeliveryCall ? 'Chamando...' : 'Chamar motoboy - R$ 4,00'}
          </Button>
        </DialogActions>
      </Dialog>

      <Box display={activeTab === 'orders' ? 'flex' : 'none'} alignItems="center" justifyContent="center" gap={2} mt={2}>
        <Button
          variant="outlined"
          disabled={page === 0 || loading}
          onClick={() => setPage((current) => Math.max(0, current - 1))}
        >
          Anterior
        </Button>
        <Typography variant="body2">Pagina {page + 1}</Typography>
        <Button
          variant="outlined"
          disabled={!hasNextPage || loading}
          onClick={() => setPage((current) => current + 1)}
        >
          Proxima
        </Button>
      </Box>

      {/* MODAL DE DETALHES CORRIGIDO */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={() => setDetailDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{ style: { borderRadius: 16, padding: 8 } }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333' }}>Pedido #{selectedPedido?.lanchonete_order_number || selectedPedido?.id}</Typography>
              <Typography variant="caption" color="textSecondary">ID único: {selectedPedido?.id}</Typography>
            </Box>
            <IconButton onClick={() => setDetailDialogOpen(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ borderBottom: 'none' }}>
          {selectedPedido && (
            <Box>
              <Box mb={4}>
                <Typography variant="subtitle2" sx={{ color: '#E50914', fontWeight: 'bold', mb: 2, textTransform: 'uppercase' }}>📍 Informações de Entrega</Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 2 }}>
                  <Grid container spacing={1}>
                    <Grid item xs={12}><Typography variant="body2"><strong>Cliente:</strong> {selectedPedido.customer_name || 'Não informado'}</Typography></Grid>
                    <Grid item xs={12}><Typography variant="body2"><strong>Telefone:</strong> {selectedPedido.customer_phone}</Typography></Grid>
                    <Grid item xs={12}><Typography variant="body2"><strong>Endereço:</strong> {selectedPedido.delivery_address}</Typography></Grid>
                    <Grid item xs={12}><Typography variant="body2"><strong>Nº Casa:</strong> {selectedPedido.house_number || 'Não informado'}</Typography></Grid>
                    <Grid item xs={12}><Typography variant="body2"><strong>Referência:</strong> {selectedPedido.reference || 'Não informada'}</Typography></Grid>
                  </Grid>
                </Paper>
              </Box>

              {selectedPedido.motoboy_name && (
                <Box mb={4}>
                  <Typography variant="subtitle2" sx={{ color: '#E50914', fontWeight: 'bold', mb: 2, textTransform: 'uppercase' }}>Entregador responsavel</Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fff7f7', borderRadius: 2, borderColor: '#fecaca' }}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <TwoWheelerIcon color="primary" />
                      <Box>
                        <Typography fontWeight="bold">{selectedPedido.motoboy_name}</Typography>
                        {selectedPedido.motoboy_phone && (
                          <Typography variant="body2" color="text.secondary">{selectedPedido.motoboy_phone}</Typography>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                </Box>
              )}

              <Box mb={2}>
                <Typography variant="subtitle2" sx={{ color: '#E50914', fontWeight: 'bold', mb: 2, textTransform: 'uppercase' }}>🍔 Itens do Pedido</Typography>
                <List disablePadding>
                  {selectedPedido.itens_corrigidos?.map((item, index) => (
                    <Box key={index} sx={{ mb: 2, pb: 2, borderBottom: '1px dashed #eee' }}>
                      <Box display="flex" justifyContent="space-between">
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            <span style={{ color: '#E50914' }}>{item.quantity}x</span> {item.produto_name}
                          </Typography>
                          {item.observation && (
                            <Box sx={{ mt: 1, p: 1, bgcolor: '#FFF1F2', borderRadius: 1, borderLeft: '3px solid #E50914' }}>
                              <Typography variant="caption" color="textSecondary">📝 Obs: {item.observation}</Typography>
                            </Box>
                          )}
                          
                          {/* Personalizações */}
                          {item.customizations && item.customizations.length > 0 && (
                            <Box sx={{ mt: 2, p: 2, bgcolor: '#f0f8ff', borderRadius: 1, borderLeft: '3px solid #2196F3' }}>
                              <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold', mb: 1, display: 'block' }}>
                                🎨 Personalizações:
                              </Typography>
                              {item.customizations.map((customization, customIndex) => (
                                <Box key={customIndex} sx={{ mb: 1.5 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#1976D2', display: 'block' }}>
                                    {customization.group_name}:
                                  </Typography>
                                  {customization.selected_options.map((option, optionIndex) => (
                                    <Box key={optionIndex} sx={{ ml: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <Typography variant="caption" color="textSecondary">
                                        • {option.name}
                                      </Typography>
                                      {option.price > 0 && (
                                        <Typography variant="caption" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
                                          +R$ {option.price.toFixed(2)}
                                        </Typography>
                                      )}
                                    </Box>
                                  ))}
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>R$ {item.subtotal?.toFixed(2)}</Typography>
                      </Box>
                    </Box>
                  ))}
                </List>
              </Box>

              <Box sx={{ mt: 3, p: 2, bgcolor: '#333', borderRadius: 2, color: '#fff' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6">Total do Pedido</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#E50914' }}>
                    R$ {selectedPedido.total_amount?.toFixed(2) || '0.00'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          {selectedPedido &&
            ['paid', 'partially_refunded'].includes(String(selectedPedido.payment_status || '').toLowerCase()) &&
            (String(selectedPedido.refund_status || '').toLowerCase() !== 'processing' || isRefundRetryable(selectedPedido)) &&
            String(selectedPedido.payment_provider || 'mercadopago').toLowerCase() === 'mercadopago' && (
              <Button color="error" variant="contained" onClick={openRefundDialog}>
                {isRefundRetryable(selectedPedido)
                  ? 'Conferir estorno pendente'
                  : 'Devolver pagamento'}
              </Button>
            )}
          <Button onClick={() => setDetailDialogOpen(false)} variant="outlined" sx={{ borderRadius: 8, px: 4 }}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={refundDialogOpen} onClose={() => !refunding && setRefundDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>
          {isRefundRetryable(selectedPedido) ? 'Conferir estorno' : 'Devolver pagamento'}
          {' '}do pedido #{selectedPedido?.lanchonete_order_number || selectedPedido?.id}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isRefundRetryable(selectedPedido)
              ? 'Esta tentativa nao recebeu confirmacao do provedor. O valor salvo sera reenviado com a mesma chave para evitar um segundo estorno.'
              : 'O Mercado Pago retirara proporcionalmente o valor da lanchonete e a taxa do All Delivery. Para devolver tudo, mantenha o valor total restante.'}
          </Typography>
          <TextField
            fullWidth
            label="Valor da devolucao"
            value={refundForm.amount}
            disabled={refunding || isRefundRetryable(selectedPedido)}
            onChange={(event) => setRefundForm((current) => ({ ...current, amount: event.target.value }))}
            inputProps={{ inputMode: 'decimal' }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            required
            multiline
            minRows={3}
            label="Motivo da devolucao"
            value={refundForm.reason}
            onChange={(event) => setRefundForm((current) => ({ ...current, reason: event.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundDialogOpen(false)} disabled={refunding}>Voltar</Button>
          <Button color="error" variant="contained" onClick={handleRefund} disabled={refunding}>
            {refunding ? 'Devolvendo...' : 'Confirmar devolucao'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default Pedidos;
